/**
 * AuthModal — Feb 2026 sleek rewrite.
 *
 * Design goals:
 *   • Sign IN is the default. Sign UP is a small footer link.
 *   • Modern, minimal card. Google button on top; single divider; one form.
 *   • Sign-in has two lightweight tabs (Mobile OTP / Email password) — no
 *     giant pill toggle, just a text-underline tab bar.
 *   • Sign-up = 4 short fields (name, mobile, email, optional partner code)
 *     + guardian consent + single "Send OTP" CTA. Backend sends a SINGLE code
 *     to BOTH channels; user enters it once and is fully verified — no more
 *     OTP anywhere.
 *   • Duplicate-mobile pre-check (public/mobile-availability). If claimed, an
 *     inline card offers "Sign in with OTP →" or "Change number".
 *   • Google consent is captured POST-return via a full-screen gate on the
 *     dashboard (see PostGoogleConsentGate.jsx) — this modal stays focused.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import useWebOtp from "@/hooks/useWebOtp";
import {
  GUARDIAN_DECLARATION_TEXT,
  GUARDIAN_DECLARATION_VERSION,
  TERMS_VERSION_IN_EFFECT,
} from "@/constants/legalDisclosures";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Phone, Mail, User, Lock, ShieldCheck, ArrowRight, ArrowLeft, Loader2, AlertCircle, AlertTriangle, Tag, Check, X, KeyRound, Fingerprint, PlayCircle,
} from "lucide-react";
import { isPlatformAuthenticatorAvailable, signinWithPasskey } from "@/lib/webauthn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PENDING_REF_KEY = "dhruva_pending_partner_code";
const PENDING_INTENT_KEY = "dhruva_pending_intent";
const PENDING_SIGNUP_OTP_KEY = "dhruva_pending_signup_otp";
// Mobile number captured BEFORE Google OAuth kicks off. Persists across the
// full-page OAuth redirect; PostGoogleConsentGate picks it up on return and
// silently attaches it to the freshly-created user record.
const PENDING_GOOGLE_MOBILE_KEY = "dhruva_pending_google_mobile";
export { PENDING_GOOGLE_MOBILE_KEY };

// ── Mobile normalisation shared by Sign-in and Sign-up ─────────────────────
function normaliseMobile(raw) {
  const digits = (raw || "").replace(/[^\d]/g, "");
  if ((raw || "").trim().startsWith("+")) return "+" + digits;
  if (digits.length === 10) return "+91" + digits;
  if (digits.length === 12 && digits.startsWith("91")) return "+" + digits;
  return digits ? "+" + digits : "";
}
const isMobileValid = (m) => /^\+\d{10,14}$/.test(normaliseMobile(m));

export default function AuthModal({ open, onOpenChange, mode = "signin", onModeChange, intent = null }) {
  const navigate = useNavigate();

  const handleOpenChange = (next) => {
    if (!next) {
      let hadPendingOtp = false;
      try {
        hadPendingOtp = sessionStorage.getItem(PENDING_SIGNUP_OTP_KEY) === "1";
        sessionStorage.removeItem(PENDING_SIGNUP_OTP_KEY);
      } catch (_) { /* privacy mode */ }
      if (hadPendingOtp) {
        setTimeout(() => {
          try {
            if (window.location.pathname === "/" || window.location.pathname === "") {
              navigate("/dashboard", { replace: true });
            }
          } catch (_) { /* noop */ }
        }, 0);
      }
    }
    onOpenChange?.(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md p-0 gap-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        data-testid="auth-modal"
      >
        <DialogTitle className="sr-only">Sign in to AdmissionCrafts</DialogTitle>
        <DialogDescription className="sr-only">
          Sign in with mobile OTP, email, or Google. New here? Create an account below.
        </DialogDescription>

        <StudentPanel
          mode={mode}
          onModeChange={onModeChange}
          intent={intent}
          onClose={() => handleOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// STUDENT PANEL — Sign-in default; sign-up is a link.
// =============================================================================
function StudentPanel({ mode, onModeChange, intent, onClose }) {
  const isSignup = mode === "signup";

  return (
    <div className="p-5 sm:p-7">
      <div className="mb-4">
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900" data-testid="auth-title">
          {isSignup ? "Create your account" : "Welcome back"}
        </h2>
        <p className="text-[12px] text-slate-500 mt-1" data-testid="auth-subtitle">
          {isSignup
            ? "One quick sign-up. One OTP. Then just your PIN."
            : "Sign in with your 4-digit PIN, or continue with Google."}
        </p>
      </div>

      <GoogleButton intent={intent} isSignup={isSignup} />
      <Divider label="or" />

      {isSignup ? (
        <SignupFlow intent={intent} onSwitchSignin={() => onModeChange("signin")} onClose={onClose} />
      ) : (
        <SigninFlow onSwitchSignup={() => onModeChange("signup")} onClose={onClose} />
      )}

      <p className="text-[10px] text-slate-400 text-center mt-5" data-testid="auth-terms-note">
        By continuing you agree to our{" "}
        <Link to="/terms" className="text-slate-700 underline" onClick={onClose}>Terms</Link>
        {" "}&amp;{" "}
        <Link to="/privacy" className="text-slate-700 underline" onClick={onClose}>Privacy Policy</Link>.
      </p>
    </div>
  );
}

// =============================================================================
// Google button — Feb 2026: captures mobile number BEFORE OAuth redirect so
// Google-SIGNUP users always have a usable mobile on file for lifecycle SMS,
// WhatsApp receipts and support. The captured mobile is stashed in
// localStorage under PENDING_GOOGLE_MOBILE_KEY; PostGoogleConsentGate reads
// and silently attaches it after the OAuth callback returns.
//
// Jul 24, 2026 — sign-IN with Google now SKIPS the mobile-capture step. The
// user already has a mobile stored server-side from their original signup,
// so re-collecting adds friction with zero payoff. The `isSignup` prop
// (defaults to false for safety) gates the capture phase.
// =============================================================================
const GoogleSvg = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

function GoogleButton({ intent, isSignup = false }) {
  const [phase, setPhase] = useState("idle"); // 'idle' | 'capture' | 'redirecting'
  const [mobile, setMobile] = useState("");
  const [dupMobile, setDupMobile] = useState(null); // { verified, mobile } | null
  const [err, setErr] = useState("");

  const mobileValid = isMobileValid(mobile);

  // Debounced duplicate-mobile check: mirrors the sign-up flow. If already
  // claimed, we DO NOT block — we just surface a soft hint so the user can
  // pick a different number or continue if it's really theirs. (Backend
  // /users/me/mobile-unverified enforces the hard 409.)
  useEffect(() => {
    if (phase !== "capture" || !mobileValid) { setDupMobile(null); return; }
    const norm = normaliseMobile(mobile);
    const handle = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/public/mobile-availability?mobile=${encodeURIComponent(norm)}`);
        if (!r.ok) return;
        const data = await r.json();
        if (data.available) setDupMobile(null);
        else setDupMobile({ verified: !!data.verified, mobile: norm });
      } catch (_) { /* offline — ignore */ }
    }, 450);
    return () => clearTimeout(handle);
  }, [mobile, mobileValid, phase]);

  const startOAuth = async () => {
    setErr("");
    if (!mobileValid) { setErr("Enter a valid 10-digit mobile number."); return; }
    const norm = normaliseMobile(mobile);
    setPhase("redirecting");
    try {
      localStorage.setItem(PENDING_GOOGLE_MOBILE_KEY, norm);
      if (intent) localStorage.setItem(PENDING_INTENT_KEY, intent);
    } catch (_) { /* privacy mode — user will see the fallback gate */ }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: { access_type: "offline", prompt: "consent" },
      },
    });
    if (error) {
      setPhase("capture");
      toast.error(error.message || "Could not start Google sign-in");
    }
  };

  // Sign-IN path skips the mobile capture entirely — the user already has a
  // mobile on file from their original signup. Kick straight to OAuth.
  const startOAuthDirect = async () => {
    setPhase("redirecting");
    try {
      if (intent) localStorage.setItem(PENDING_INTENT_KEY, intent);
    } catch (_) { /* privacy mode — non-critical */ }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: { access_type: "offline", prompt: "consent" },
      },
    });
    if (error) {
      setPhase("idle");
      toast.error(error.message || "Could not start Google sign-in");
    }
  };

  if (phase === "idle") {
    return (
      <button
        type="button"
        onClick={isSignup ? () => setPhase("capture") : startOAuthDirect}
        className="w-full h-11 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-900 text-[13.5px] font-semibold flex items-center justify-center gap-2 transition-colors"
        data-testid="auth-google-button"
      >
        <GoogleSvg />
        Continue with Google
      </button>
    );
  }

  if (phase === "redirecting" && !isSignup) {
    return (
      <button
        type="button"
        disabled
        className="w-full h-11 rounded-lg border border-slate-300 bg-slate-50 text-slate-500 text-[13.5px] font-semibold flex items-center justify-center gap-2"
        data-testid="auth-google-button"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Google…
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="google-mobile-capture">
      <div className="flex items-center gap-2 mb-2">
        <GoogleSvg />
        <p className="text-[12px] text-slate-700 font-semibold">Enter your mobile to continue with Google</p>
      </div>
      <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">
        We use this for payment receipts and prediction updates. No OTP right now — Google verifies your email.
      </p>
      <div className="relative">
        <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)}
          placeholder="10-digit mobile (e.g. 98765 43210)"
          className="h-11 pl-9 text-sm bg-white"
          autoComplete="tel" maxLength={15}
          onKeyDown={(e) => { if (e.key === "Enter" && mobileValid) startOAuth(); }}
          data-testid="google-mobile-input" autoFocus
          disabled={phase === "redirecting"}
        />
      </div>
      {dupMobile && (
        <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-start gap-1.5" data-testid="google-mobile-dup-hint">
          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>This mobile is already registered. If it&apos;s yours, continue — otherwise use sign-in.</span>
        </div>
      )}
      {err && (
        <div className="mt-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5" data-testid="google-mobile-error">
          {err}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button
          type="button"
          onClick={startOAuth}
          disabled={!mobileValid || phase === "redirecting"}
          className="flex-1 h-10 bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-semibold"
          data-testid="google-mobile-continue"
        >
          {phase === "redirecting" ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
          ) : (
            <>Continue with Google <ArrowRight className="w-4 h-4 ml-1" /></>
          )}
        </Button>
        <button
          type="button"
          onClick={() => { setPhase("idle"); setMobile(""); setDupMobile(null); setErr(""); }}
          disabled={phase === "redirecting"}
          className="text-[11.5px] text-slate-500 hover:text-slate-800 px-2 h-10"
          data-testid="google-mobile-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Divider({ label }) {
  return (
    <div className="relative my-4">
      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
      <div className="relative flex justify-center">
        <span className="bg-white px-2 text-[10px] tracking-[0.2em] uppercase text-slate-400">{label}</span>
      </div>
    </div>
  );
}

// =============================================================================
// SIGN-IN — PIN-based (Feb 2026). Users enter an email or mobile + 4-digit PIN.
// OTP is only used for Forgot-PIN recovery and first-time PIN setup.
// =============================================================================
function SigninFlow({ onSwitchSignup, onClose }) {
  const [step, setStep] = useState("pin"); // 'pin' | 'password' | 'forgot' | 'reset'
  const [identifier, setIdentifier] = useState("");
  const [delivery, setDelivery] = useState(null);

  // Feb 2026 (defect NEET-6): if the user landed here via the "Sign in
  // with OTP" link on the signup dup-mobile card, pre-fill their mobile
  // and skip the PIN step in favour of the OTP-based recovery flow —
  // that's the code path that sends an OTP to their number.
  useEffect(() => {
    try {
      const pendingMobile = sessionStorage.getItem("dhruva_pending_signin_mobile");
      const pendingMode = sessionStorage.getItem("dhruva_pending_signin_mode");
      if (pendingMode === "otp" && pendingMobile) {
        setIdentifier(pendingMobile);
        setStep("forgot");
      }
    } catch (_) { /* noop */ }
    // Always clear the flag so a subsequent manual "Sign in" doesn't
    // accidentally jump into the forgot flow.
    try {
      sessionStorage.removeItem("dhruva_pending_signin_mobile");
      sessionStorage.removeItem("dhruva_pending_signin_mode");
    } catch (_) { /* noop */ }
  }, []);

  if (step === "forgot") {
    return (
      <ForgotPinFlow
        identifier={identifier}
        setIdentifier={setIdentifier}
        onSent={(d) => { setDelivery(d); setStep("reset"); }}
        onBack={() => setStep("pin")}
      />
    );
  }
  if (step === "reset") {
    return (
      <ResetPinFlow
        identifier={identifier}
        delivery={delivery}
        onDone={onClose}
        onBack={() => setStep("forgot")}
      />
    );
  }
  if (step === "password") {
    return (
      <PasswordSigninForm
        identifier={identifier}
        setIdentifier={setIdentifier}
        onSwitchPin={() => setStep("pin")}
        onSwitchSignup={onSwitchSignup}
        onSignedIn={onClose}
      />
    );
  }
  return (
    <PinSigninForm
      identifier={identifier}
      setIdentifier={setIdentifier}
      onForgot={() => setStep("forgot")}
      onSwitchPassword={() => setStep("password")}
      onSwitchSignup={onSwitchSignup}
      onSignedIn={onClose}
    />
  );
}

// =============================================================================
// PASSWORD sign-in — direct Supabase signInWithPassword. Used by:
//   1. Admin-seeded dummy users (@dhruva-demo.test / DhruvaDemo@2026)
//   2. Any user whose password was set/reset by admin via
//      POST /api/admin/users/set-password
// PIN remains the primary path; this is the "known password" escape hatch
// per the Feb 2026 integration playbook.
// =============================================================================
function PasswordSigninForm({ identifier, setIdentifier, onSwitchPin, onSwitchSignup, onSignedIn }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!identifier.trim() || !identifier.includes("@")) {
      setErr("Enter your email address");
      return;
    }
    if (!password) { setErr("Enter your password"); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: identifier.trim().toLowerCase(),
        password,
      });
      if (error) {
        // Playbook: generic error only, no user enumeration.
        setErr("Invalid email or password");
        return;
      }
      toast.success("Signed in!");
      onSignedIn?.();
    } catch (e2) {
      setErr(e2?.message || "Could not sign in");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="auth-password-signin-form">
      <IconInput
        icon={User} type="email" placeholder="Email address"
        value={identifier} onChange={(e) => setIdentifier(e.target.value)}
        autoComplete="username" testid="auth-password-identifier-input"
      />
      <div className="relative">
        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          type="password" placeholder="Password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          className="h-11 pl-9 text-sm"
          data-testid="auth-password-input"
        />
      </div>
      {err && <p className="text-[11px] text-rose-700" data-testid="auth-password-error">{err}</p>}
      <Button type="submit" disabled={busy} className="w-full h-11 text-sm font-semibold bg-slate-900 hover:bg-slate-800" data-testid="auth-password-submit">
        {busy ? "Signing in…" : "Sign in with password"}
      </Button>

      <div className="flex items-center justify-between text-[11.5px] pt-0.5">
        <button
          type="button" onClick={onSwitchPin}
          className="text-slate-600 hover:text-slate-900 underline underline-offset-2"
          data-testid="auth-switch-to-pin"
        >
          Use PIN instead
        </button>
        <button
          type="button" onClick={onSwitchSignup}
          className="text-slate-900 font-semibold underline"
          data-testid="auth-password-switch-to-signup"
        >
          Create account
        </button>
      </div>
    </form>
  );
}

function TabButton({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 pb-2 -mb-px text-[12.5px] font-semibold transition-colors ${
        active
          ? "text-slate-900 border-b-2 border-slate-900"
          : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent"
      }`}
    >
      {children}
    </button>
  );
}

function PinSigninForm({ identifier, setIdentifier, onForgot, onSwitchPassword, onSwitchSignup, onSignedIn }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  useEffect(() => {
    // Cheap capability check — only show the passkey button if the browser
    // supports it AND the device has a platform authenticator (Face ID etc.).
    isPlatformAuthenticatorAvailable().then(setPasskeyAvailable);
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!identifier.trim()) { setErr("Enter your email or mobile"); return; }
    if (!/^\d{4}$/.test(pin)) { setErr("Enter your 4-digit PIN"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/pin/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim(), pin }),
      });
      const data = await r.json();
      if (!r.ok || !data.verified) {
        setErr(data.reason || "Incorrect email/mobile or PIN");
        return;
      }
      if (!data.token_hash) { setErr("Could not start session — try again"); return; }
      const { error } = await supabase.auth.verifyOtp({ token_hash: data.token_hash, type: "magiclink" });
      if (error) { setErr(error.message || "Could not start session"); return; }
      toast.success("Signed in!");
      onSignedIn?.();
    } catch (e2) {
      setErr(e2?.message || "Could not verify PIN");
    } finally { setBusy(false); }
  };

  const doPasskey = async () => {
    setErr("");
    if (!identifier.trim()) { setErr("Enter your email or mobile above first"); return; }
    setPasskeyBusy(true);
    try {
      const { token_hash } = await signinWithPasskey(identifier.trim());
      if (!token_hash) throw new Error("No session token from server");
      const { error } = await supabase.auth.verifyOtp({ token_hash, type: "magiclink" });
      if (error) throw new Error(error.message || "Could not start session");
      toast.success("Signed in with Face ID!");
      onSignedIn?.();
    } catch (e2) {
      const msg = e2?.message || "Passkey sign-in failed";
      // NotAllowedError = user cancelled — friendly toast, not a red bar.
      if (msg.toLowerCase().includes("cancel") || msg.toLowerCase().includes("notallowed")) {
        toast.info("Passkey sign-in cancelled");
      } else {
        setErr(msg);
      }
    } finally { setPasskeyBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="auth-pin-signin-form">
      <IconInput
        icon={User} type="text" placeholder="Email or mobile"
        value={identifier} onChange={(e) => setIdentifier(e.target.value)}
        autoComplete="username" testid="auth-pin-identifier-input"
      />

      <div className="relative">
        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
          placeholder="4-digit PIN" value={pin}
          onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
          autoComplete="current-password"
          className="h-11 pl-9 text-sm tracking-[0.4em] font-mono"
          data-testid="auth-pin-input"
        />
      </div>
      {err && <p className="text-[11px] text-rose-700" data-testid="auth-pin-error">{err}</p>}
      <Button
        type="submit"
        className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold mt-1"
        disabled={busy || pin.length !== 4 || !identifier.trim()}
        data-testid="auth-pin-signin-button"
      >
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {busy ? "Signing in…" : "Sign in"}
      </Button>

      {/* Face ID / Touch ID — subtle icon-chip below the primary CTA (was
          a big bordered button; user feedback Feb 2026 flagged it as too
          heavy for the compact modal). Only rendered if the device
          supports it AND the browser reports a platform authenticator. */}
      {passkeyAvailable && (
        <button
          type="button" onClick={doPasskey} disabled={passkeyBusy}
          className="w-full inline-flex items-center justify-center gap-1.5 h-8 text-[11.5px] font-semibold text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-60"
          data-testid="auth-passkey-button"
          title="Use Face ID / Touch ID / Windows Hello"
        >
          {passkeyBusy ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Fingerprint className="w-3 h-3" />
          )}
          {passkeyBusy ? "Waiting for device…" : "Use Face ID instead"}
        </button>
      )}

      <div className="flex items-center justify-between text-[11.5px] pt-0.5">
        <button
          type="button" onClick={onForgot}
          className="text-slate-600 hover:text-slate-900 underline underline-offset-2"
          data-testid="auth-pin-forgot"
        >
          Forgot PIN?
        </button>
        <button
          type="button" onClick={onSwitchSignup}
          className="text-slate-900 font-semibold underline"
          data-testid="auth-switch-to-signup"
        >
          Create account
        </button>
      </div>

      {/* Password sign-in escape hatch — for users who signed up with an
          admin-set password (dummy demo cohort, admin-recovered accounts).
          Small, low-emphasis link so PIN remains the primary path. */}
      {onSwitchPassword && (
        <button
          type="button" onClick={onSwitchPassword}
          className="text-[11px] text-slate-500 hover:text-slate-800 underline underline-offset-2"
          data-testid="auth-switch-to-password"
        >
          Sign in with password instead
        </button>
      )}

      {/* Login-help video — small text link that opens the video guide in a
          new tab. Keeps the modal compact but gives users an escape hatch
          when they get stuck (KapSystem SMS outage, forgotten PIN, etc.). */}
      <a
        href="https://youtu.be/BfhJEwREO4s"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-indigo-700 hover:text-indigo-900 underline underline-offset-2"
        data-testid="auth-login-help-video"
      >
        <PlayCircle className="w-3 h-3" /> Watch: how to sign in (60 sec)
      </a>
    </form>
  );
}

function ForgotPinFlow({ identifier, setIdentifier, onSent, onBack }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!identifier.trim()) { setErr("Enter your email or mobile"); return; }
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API}/auth/pin/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });
      const data = await r.json();
      // Anti-enumeration: server always 200. Show generic success either way.
      if (!r.ok) { setErr("Could not send code — try again"); return; }
      toast.success(data.channels?.length
        ? `Code sent via ${data.channels.join(" + ")}`
        : "If your account exists, we sent a code");
      onSent?.({
        channels: data.channels || [],
        email_masked: data.email_masked || null,
        sms_gateway_down: !!data.sms_gateway_down,
      });
    } catch (e2) {
      setErr(e2?.message || "Could not send code");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="auth-forgot-pin-form">
      <p className="text-[12px] text-slate-600">
        Enter your email or mobile — we&apos;ll send a one-time code to reset your PIN.
      </p>
      <IconInput
        icon={User} type="text" placeholder="Email or mobile"
        value={identifier} onChange={(e) => setIdentifier(e.target.value)}
        autoComplete="username" testid="auth-forgot-pin-identifier-input"
      />
      {err && <p className="text-[11px] text-rose-700">{err}</p>}
      <Button
        type="submit"
        className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
        disabled={busy || !identifier.trim()}
        data-testid="auth-forgot-pin-send"
      >
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {busy ? "Sending…" : "Send code"}
      </Button>
      <button type="button" onClick={onBack} className="text-[11.5px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to sign in
      </button>
    </form>
  );
}

function ResetPinFlow({ identifier, delivery, onDone, onBack }) {
  const [otp, setOtp] = useState("");
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const smsDown = !!delivery?.sms_gateway_down;
  const channels = delivery?.channels || [];
  const emailOnly = channels.length === 1 && channels[0] === "email";
  const emailMasked = delivery?.email_masked;

  const submit = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(otp)) { setErr("Enter the 6-digit code"); return; }
    if (!/^\d{4}$/.test(newPin)) { setErr("New PIN must be 4 digits"); return; }
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API}/auth/pin/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim(), otp, new_pin: newPin }),
      });
      const data = await r.json();
      if (!r.ok || !data.ok) { setErr(data.reason || "Could not reset PIN"); return; }
      if (data.token_hash) {
        const { error } = await supabase.auth.verifyOtp({ token_hash: data.token_hash, type: "magiclink" });
        if (!error) {
          toast.success("PIN updated — you're signed in.");
          onDone?.();
          return;
        }
      }
      toast.success("PIN updated — please sign in with your new PIN.");
      onBack?.();
    } catch (e2) {
      setErr(e2?.message || "Could not reset PIN");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="auth-reset-pin-form">
      {(smsDown || emailOnly) && emailMasked ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-[11.5px] text-amber-900 flex items-start gap-2" data-testid="auth-reset-pin-email-hint">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            SMS is temporarily unavailable. We&apos;ve sent your code by <b>email</b> to{" "}
            <span className="font-mono">{emailMasked}</span>. Please check your inbox (and spam).
          </div>
        </div>
      ) : (
        <p className="text-[12px] text-slate-600">
          We sent a 6-digit code to <strong>{identifier}</strong>. Enter it below along with a new PIN.
          {emailMasked && !smsDown && (
            <> <span className="text-slate-500">(Also sent to {emailMasked}.)</span></>
          )}
        </p>
      )}
      <Input
        inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code"
        placeholder="6-digit code" value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
        className="h-11 text-center text-lg font-mono tracking-[0.4em]"
        data-testid="auth-reset-pin-otp"
      />
      <Input
        type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
        placeholder="New 4-digit PIN" value={newPin}
        onChange={(e) => setNewPin(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
        className="h-11 text-center text-lg font-mono tracking-[0.5em]"
        data-testid="auth-reset-pin-new"
      />
      {err && <p className="text-[11px] text-rose-700">{err}</p>}
      <Button
        type="submit"
        className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
        disabled={busy || otp.length !== 6 || newPin.length !== 4}
        data-testid="auth-reset-pin-submit"
      >
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {busy ? "Updating…" : "Update PIN & sign in"}
      </Button>
      <button type="button" onClick={onBack} className="text-[11.5px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Resend code
      </button>
    </form>
  );
}

function MobileOtpSigninForm({ onSignedIn }) {
  const [step, setStep] = useState("mobile"); // 'mobile' | 'otp'
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resendIn, setResendIn] = useState(0);

  const onWebOtpCode = useCallback((code) => { setOtp(code); setErr(""); }, []);
  useWebOtp({ enabled: step === "otp", onCode: onWebOtpCode });

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const mobileValid = isMobileValid(mobile);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!mobileValid) { setErr("Enter a valid Indian mobile number"); return; }
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API}/auth/mobile/login/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: normaliseMobile(mobile) }),
      });
      if (!r.ok) throw new Error("Could not send OTP");
      setStep("otp");
      setResendIn(60);
      toast.success("Code sent to your mobile and email");
    } catch (e2) {
      setErr(e2?.message || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) { setErr("Enter the 6-digit code"); return; }
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API}/auth/mobile/login/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: normaliseMobile(mobile), otp: otp.trim() }),
      });
      const data = await r.json();
      if (!r.ok || !data.verified) { setErr(data.reason || "Incorrect code"); return; }
      if (!data.token_hash) { setErr("Could not start session"); return; }
      const { error } = await supabase.auth.verifyOtp({ token_hash: data.token_hash, type: "magiclink" });
      if (error) { setErr(error.message || "Could not start session"); return; }
      toast.success("Signed in!");
      onSignedIn?.();
    } catch (e2) {
      setErr(e2?.message || "Could not verify code");
    } finally { setBusy(false); }
  };

  if (step === "otp") {
    return (
      <form onSubmit={verifyOtp} className="space-y-3" data-testid="auth-mobile-otp-panel">
        <p className="text-[11.5px] text-slate-600">
          Code sent to <strong>{normaliseMobile(mobile)}</strong>. It’s also in your email.
        </p>
        <Input
          inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code"
          placeholder="••••••" value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
          className="h-12 text-center text-xl font-mono tracking-[0.5em]"
          data-testid="auth-mobile-otp-code-input" autoFocus
        />
        {err && <p className="text-[11px] text-rose-700" data-testid="auth-mobile-otp-error">{err}</p>}
        <Button type="submit"
          className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
          disabled={busy || otp.length !== 6} data-testid="auth-mobile-otp-verify-button">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {busy ? "Verifying…" : "Verify & sign in"}
        </Button>
        <div className="flex items-center justify-between text-[11.5px]">
          <button type="button" onClick={sendOtp} disabled={resendIn > 0 || busy}
            className="font-semibold text-slate-700 hover:text-slate-900 disabled:text-slate-400 underline underline-offset-2">
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
          <button type="button" onClick={() => { setStep("mobile"); setOtp(""); setErr(""); }}
            className="text-slate-500 hover:text-slate-700" data-testid="auth-mobile-otp-back">
            <ArrowLeft className="w-3 h-3 inline" /> Change number
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={sendOtp} className="space-y-2.5" data-testid="auth-mobile-otp-mobile-form">
      <IconInput icon={Phone} type="tel" placeholder="10-digit mobile (e.g. 91755 71090)"
        value={mobile} onChange={(e) => setMobile(e.target.value)} autoComplete="tel" maxLength={15}
        testid="auth-mobile-otp-mobile-input" />
      {err && <p className="text-[11px] text-rose-700" data-testid="auth-mobile-otp-error">{err}</p>}
      <Button type="submit"
        className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
        disabled={busy || !mobileValid} data-testid="auth-mobile-otp-send-button">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {busy ? "Sending code…" : "Send OTP"}
        {!busy && <ArrowRight className="w-4 h-4 ml-2" />}
      </Button>
    </form>
  );
}

// =============================================================================
// SIGN-UP — 4 fields + consent → single OTP → verified.
// =============================================================================
function SignupFlow({ intent, onSwitchSignin, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("details"); // 'details' | 'otp' | 'set-pin' | 'verify-email'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [partnerCode, setPartnerCode] = useState("");
  const [validatedCode, setValidatedCode] = useState(null);
  const [guardianDecl, setGuardianDecl] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [session, setSession] = useState(null);
  const [portalBlock, setPortalBlock] = useState(null);
  const [dupMobile, setDupMobile] = useState(null); // { verified: bool } if mobile taken
  const [resendIn, setResendIn] = useState(0);

  const onWebOtpCode = useCallback((code) => { setOtp(code); setErr(""); }, []);
  useWebOtp({ enabled: step === "otp", onCode: onWebOtpCode });

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const mobileValid = isMobileValid(mobile);
  const emailValid = email.includes("@") && email.length > 4;
  const nameValid = name.trim().length >= 2;
  const detailsValid = nameValid && emailValid && mobileValid;

  // Debounced duplicate-mobile check when the user leaves the field.
  useEffect(() => {
    if (!mobileValid) { setDupMobile(null); return; }
    const norm = normaliseMobile(mobile);
    const handle = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/public/mobile-availability?mobile=${encodeURIComponent(norm)}`);
        if (!r.ok) return;
        const data = await r.json();
        if (data.available) setDupMobile(null);
        else setDupMobile({ verified: !!data.verified, mobile: norm });
      } catch (_) { /* offline — ignore */ }
    }, 500);
    return () => clearTimeout(handle);
  }, [mobile, mobileValid]);

  const autoPassword = () => {
    const rand = crypto.getRandomValues(new Uint8Array(24));
    const b64 = btoa(String.fromCharCode(...rand)).replace(/[+/=]/g, "");
    return `Ac!${b64.slice(0, 20)}`;
  };

  const submitDetails = async (e) => {
    e?.preventDefault();
    setErr(""); setPortalBlock(null);
    if (!detailsValid) {
      setErr(!nameValid ? "Enter your full name" : !emailValid ? "Enter a valid email" : "Enter a valid mobile");
      return;
    }
    if (dupMobile) {
      setErr("This mobile is already registered — sign in with OTP or change the number.");
      return;
    }
    if (!guardianDecl) {
      setErr("Please tick the parent/guardian declaration to continue.");
      return;
    }
    setBusy(true);
    try {
      // Portal-account block (analyst / partner / admin emails)
      try {
        const r = await fetch(`${API}/public/email-availability?email=${encodeURIComponent(email)}`);
        if (r.ok) {
          const avail = await r.json();
          if (!avail.available && (avail.reason || "").startsWith("internal_")) {
            const roleLabel = (avail.reason || "").replace("internal_", "");
            setPortalBlock({ roleLabel, portalUrl: avail.portal_url || "/portal/login" });
            return;
          }
        }
      } catch (_) { /* transient */ }

      if (intent) { try { localStorage.setItem(PENDING_INTENT_KEY, intent); } catch (_) { /* storage */ } }
      if (validatedCode) { try { localStorage.setItem(PENDING_REF_KEY, validatedCode); } catch (_) { /* storage */ } }

      const pwd = autoPassword();
      try { sessionStorage.setItem(PENDING_SIGNUP_OTP_KEY, "1"); } catch (_) { /* privacy */ }
      const { data, error } = await supabase.auth.signUp({
        email, password: pwd,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: { phone: normaliseMobile(mobile), display_name: name.trim(), full_name: name.trim() },
        },
      });
      if (error) {
        setErr(error.message || "Could not create account");
        try { sessionStorage.removeItem(PENDING_SIGNUP_OTP_KEY); } catch (_) { /* noop */ }
        return;
      }

      if (!data.session) {
        try { sessionStorage.removeItem(PENDING_SIGNUP_OTP_KEY); } catch (_) { /* noop */ }
        setStep("verify-email");
        return;
      }
      setSession(data.session);

      // Persist phone to Mongo `users.mobile` immediately so the
      // PostGoogleConsentGate doesn't trigger on first dashboard visit for
      // email-flow signups. Previously we only wrote phone into Supabase
      // user_metadata via signUp options, which never reached Mongo.
      // Non-blocking on transient failures (a 409 duplicate is surfaced as
      // an error so the user knows before the account is stranded).
      try {
        const mobileResp = await fetch(`${API}/users/me/mobile-unverified`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${data.session.access_token}`,
          },
          body: JSON.stringify({ mobile: normaliseMobile(mobile) }),
        });
        if (mobileResp.status === 409) {
          const j = await mobileResp.json().catch(() => ({}));
          setErr(j?.detail || "This mobile is already registered to a different account.");
          setDupMobile({ verified: true });
          try { sessionStorage.removeItem(PENDING_SIGNUP_OTP_KEY); } catch (_) { /* noop */ }
          setBusy(false);
          return;
        }
      } catch (_) { /* transient; PostGoogleConsentGate will backfill later */ }

      // Guardian declaration (append-only, idempotent)
      try {
        await fetch(`${API}/users/guardian-declaration`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${data.session.access_token}`,
          },
          body: JSON.stringify({
            declaration_text: GUARDIAN_DECLARATION_TEXT,
            declaration_version: GUARDIAN_DECLARATION_VERSION,
            terms_version: TERMS_VERSION_IN_EFFECT,
            privacy_policy_version: TERMS_VERSION_IN_EFFECT,
          }),
        });
      } catch (_) { /* non-blocking */ }

      if (validatedCode) {
        try {
          await fetch(`${API}/users/me/attach-referral`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${data.session.access_token}`,
            },
            body: JSON.stringify({ code: validatedCode }),
          });
          localStorage.removeItem(PENDING_REF_KEY);
        } catch (_) { /* non-blocking */ }
      }
      if (intent) {
        try {
          await fetch(`${API}/users/me/intent`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${data.session.access_token}`,
            },
            body: JSON.stringify({ intent }),
          });
          localStorage.removeItem(PENDING_INTENT_KEY);
        } catch (_) { /* non-blocking */ }
      }
      try {
        await fetch(`${API}/auth/email/send-verification`, {
          method: "POST",
          headers: { Authorization: `Bearer ${data.session.access_token}` },
        });
      } catch (_) { /* dashboard banner will offer Resend */ }

      try {
        const r2 = await fetch(`${API}/auth/mobile/send-otp`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${data.session.access_token}`,
          },
          body: JSON.stringify({ mobile: normaliseMobile(mobile), template: "signup" }),
        });
        if (r2.ok) {
          // Fail-open bypass: when the SMS gateway is unhealthy the server
          // auto-verifies the mobile and returns bypass_mode=true. In that
          // case we skip the OTP entry screen entirely and jump to PIN setup.
          const j2 = await r2.json().catch(() => ({}));
          if (j2.bypass_mode) {
            toast.success("Mobile verified — SMS gateway is temporarily down, but you're all set.");
            setStep("set-pin");
            setBusy(false);
            return;
          }
        } else {
          setErr("Signup created, but we could not send the OTP. Verify from your dashboard.");
        }
      } catch (_) {
        setErr("Signup created, but we could not send the OTP. Verify from your dashboard.");
      }
      setStep("otp");
      setResendIn(60);
    } finally { setBusy(false); }
  };

  const resendOtp = async () => {
    if (!session || resendIn > 0) return;
    setErr("");
    try {
      const r = await fetch(`${API}/auth/mobile/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ mobile: normaliseMobile(mobile), template: "signup" }),
      });
      // Fail-open bypass: if the SMS gateway is down, the server auto-verifies.
      // Skip the OTP screen entirely and jump to PIN setup.
      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        if (j.bypass_mode) {
          toast.success("Mobile verified — SMS gateway is temporarily down, but you're all set.");
          setStep("set-pin");
          return;
        }
      }
      setResendIn(60);
      toast.success("Code resent");
    } catch (_) {
      setErr("Could not resend code");
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) { setErr("Enter the 6-digit code"); return; }
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API}/auth/mobile/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ mobile: normaliseMobile(mobile), otp: otp.trim() }),
      });
      const data = await r.json();
      if (!r.ok || !data.verified) {
        setErr(data.reason || data.detail || "Incorrect code — please try again");
        return;
      }
      try { sessionStorage.removeItem(PENDING_SIGNUP_OTP_KEY); } catch (_) { /* noop */ }
      // OTP verified. Now the user sets a 4-digit PIN — this is their
      // primary auth factor going forward (Feb 2026). PIN is stored
      // server-side and issued a Supabase magiclink on every sign-in.
      setStep("set-pin");
    } catch (e2) {
      setErr(e2?.message || "Could not verify code");
    } finally { setBusy(false); }
  };

  if (step === "set-pin") {
    return (
      <SetPinStep
        session={session}
        onDone={() => {
          toast.success("Welcome to AdmissionCrafts!");
          // Explicit navigation to /dashboard here — do NOT rely on the
          // AuthModal's close handler to detect the PENDING_SIGNUP_OTP_KEY
          // (that flag is cleared at OTP-verify time, several steps before
          // this closes). Without this line, PIN-set success closed the
          // modal but left the user staring at the landing page — which
          // reads as "forced logout" from a user's POV. Reported and
          // reproduced on prod 23 Jul 2026.
          try { navigate("/dashboard", { replace: true }); } catch (_) { /* SSR-safe */ }
          onClose?.();
        }}
      />
    );
  }

  if (step === "verify-email") {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3.5 text-xs text-amber-900 flex items-start gap-2.5" data-testid="auth-verify-email-banner">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold">Check your email</p>
          <p className="mt-1 leading-relaxed">
            We sent a verification link to <strong>{email}</strong>. Click it, then sign in.
          </p>
          <button type="button" onClick={onSwitchSignin} className="mt-2 inline-flex items-center gap-1 font-bold underline underline-offset-2">
            Back to sign in <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  if (step === "otp") {
    return (
      <form onSubmit={verifyOtp} className="space-y-3" data-testid="auth-signup-otp-form">
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-2.5 text-[11.5px] text-emerald-900 flex items-start gap-2">
          <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold">Account created.</p>
            <p className="mt-0.5">
              6-digit code sent to <strong>{normaliseMobile(mobile)}</strong> and{" "}
              <strong>{email}</strong>. Enter whichever arrives first — one code verifies both.
            </p>
          </div>
        </div>

        <Input
          inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code"
          placeholder="••••••" value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
          className="h-12 text-center text-xl font-mono tracking-[0.5em]"
          data-testid="auth-signup-otp-input" autoFocus
        />

        {err && <p className="text-[11px] text-rose-700" data-testid="auth-signup-otp-error">{err}</p>}

        <Button type="submit"
          className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
          disabled={busy || otp.length !== 6} data-testid="auth-signup-verify-button">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {busy ? "Verifying…" : "Verify & continue"}
        </Button>

        <div className="flex items-center justify-between text-[11.5px]">
          <button type="button" onClick={resendOtp} disabled={resendIn > 0 || busy}
            className="font-semibold text-slate-700 hover:text-slate-900 disabled:text-slate-400 underline underline-offset-2"
            data-testid="auth-signup-resend">
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
          <button type="button"
            onClick={() => { setStep("details"); setOtp(""); setErr(""); try { sessionStorage.removeItem(PENDING_SIGNUP_OTP_KEY); } catch (_) { /* noop */ } }}
            className="text-slate-500 hover:text-slate-700" data-testid="auth-signup-back">
            <ArrowLeft className="w-3 h-3 inline" /> Change details
          </button>
        </div>
      </form>
    );
  }

  // step === 'details' — compact 4-field card
  return (
    <form onSubmit={submitDetails} className="space-y-2.5" data-testid="auth-signup-form">
      <IconInput icon={User} type="text" placeholder="Full name" value={name}
        onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={80}
        testid="auth-name-input" />
      <IconInput icon={Phone} type="tel" placeholder="Mobile (e.g. 91755 71090)" value={mobile}
        onChange={(e) => setMobile(e.target.value)} autoComplete="tel" maxLength={15}
        testid="auth-mobile-input" />

      {dupMobile && (
        <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-2.5 text-[11.5px] flex items-start gap-2" data-testid="auth-duplicate-mobile">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-700" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900">This mobile is already registered.</p>
            <p className="text-amber-900/85 mt-0.5">
              Sign in with OTP on this number (you can add multiple profiles under it), or use a different number.
            </p>
            <div className="mt-1.5 flex gap-2">
              <button type="button"
                onClick={() => {
                  // Feb 2026 (defect NEET-6): the OTP button used to
                  // just flip to signin's PIN screen — which was
                  // confusing for a user who was TOLD "Sign in with
                  // OTP". We now stash the current mobile so the
                  // signin panel prefills identifier and jumps
                  // straight to the forgot-PIN OTP flow.
                  try {
                    sessionStorage.setItem("dhruva_pending_signin_mobile", mobile || "");
                    sessionStorage.setItem("dhruva_pending_signin_mode", "otp");
                  } catch (_) { /* noop */ }
                  onSwitchSignin();
                }}
                className="inline-flex items-center gap-1 font-bold text-amber-900 underline underline-offset-2"
                data-testid="auth-duplicate-mobile-signin">
                <KeyRound className="w-3 h-3" /> Sign in with OTP
              </button>
              <button type="button" onClick={() => { setMobile(""); setDupMobile(null); }}
                className="text-amber-900/80 hover:text-amber-900"
                data-testid="auth-duplicate-mobile-change">
                Change number
              </button>
            </div>
          </div>
        </div>
      )}

      <IconInput icon={Mail} type="email" placeholder="Email" value={email}
        onChange={(e) => setEmail(e.target.value)} autoComplete="email"
        testid="auth-email-input" />

      <PartnerCodeField value={partnerCode} onChange={setPartnerCode} onValid={setValidatedCode} />

      <label htmlFor="signup-guardian-checkbox"
        className="flex items-start gap-2 cursor-pointer select-none rounded-lg border border-amber-300/60 bg-amber-50/40 px-2.5 py-2"
        data-testid="signup-guardian-block">
        <input
          id="signup-guardian-checkbox" type="checkbox" checked={guardianDecl}
          onChange={(e) => setGuardianDecl(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 rounded border-slate-400 accent-amber-500 shrink-0"
          data-testid="signup-guardian-checkbox" required
        />
        <span className="text-[11px] leading-relaxed text-slate-700">
          {GUARDIAN_DECLARATION_TEXT.split("Terms & Conditions and Privacy Policy")[0]}
          <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline text-slate-900">Terms</Link>{" & "}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-slate-900">Privacy</Link>.
        </span>
      </label>

      {err && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-2 text-[11px] text-rose-800 flex items-start gap-1.5" data-testid="auth-signup-error">
          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{err}</span>
        </div>
      )}

      {portalBlock && (
        <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-2.5 text-[11.5px] flex items-start gap-2" data-testid="auth-portal-block">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-700" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900">
              This email is registered as {portalBlock.roleLabel}.
            </p>
            <Link to={portalBlock.portalUrl}
              className="mt-1 inline-flex items-center gap-1 text-amber-900 font-bold underline underline-offset-2"
              data-testid="auth-portal-block-cta">
              Go to portal <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}

      <Button
        type="submit"
        className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold mt-1"
        disabled={busy || !detailsValid || !guardianDecl || !!dupMobile}
        data-testid="auth-signup-button"
      >
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {busy ? "Creating…" : "Create account & send OTP"}
      </Button>

      <div className="text-center text-[12px] text-slate-600 pt-1">
        Already have an account?{" "}
        <button type="button" onClick={onSwitchSignin}
          className="text-slate-900 font-semibold underline" data-testid="auth-switch-to-signin">
          Sign in
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// Shared helpers
// =============================================================================
function IconInput({ icon: Icon, type = "text", value, onChange, placeholder, autoComplete, maxLength, testid, pattern, inputMode }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <Input
        type={type} value={value} onChange={onChange} placeholder={placeholder}
        autoComplete={autoComplete} maxLength={maxLength} pattern={pattern} inputMode={inputMode}
        className="h-11 pl-9 text-sm" data-testid={testid}
      />
    </div>
  );
}

function PartnerCodeField({ value, onChange, onValid }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ state: "idle", message: "", partner_name: null });

  useEffect(() => {
    const v = (value || "").trim();
    if (!v || v.length < 4) {
      setStatus({ state: "idle", message: "", partner_name: null });
      onValid?.(null);
      return;
    }
    setStatus({ state: "checking", message: "Checking…", partner_name: null });
    const handle = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/public/partner-code/${encodeURIComponent(v)}`);
        const data = await r.json();
        if (data.valid) {
          setStatus({
            state: "ok",
            message: `${t("auth.partnerStatusOkPrefix", "Valid partner:")} ${data.partner_name || "Partner"}`,
            partner_name: data.partner_name,
          });
          onValid?.(v);
        } else {
          setStatus({ state: "bad", message: data.reason || "Invalid code", partner_name: null });
          onValid?.(null);
        }
      } catch (_) {
        setStatus({ state: "bad", message: "Could not verify", partner_name: null });
        onValid?.(null);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [value, onValid, t]);

  return (
    <div className="relative">
      <Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <Input
        type="text" placeholder="Partner code (optional)" value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-11 pl-9 pr-10 text-sm uppercase tracking-wider"
        data-testid="auth-partner-input" autoComplete="off" maxLength={15}
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {status.state === "checking" && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        {status.state === "ok" && <Check className="w-3.5 h-3.5 text-emerald-600" />}
        {status.state === "bad" && <X className="w-3.5 h-3.5 text-rose-500" />}
      </div>
      {status.state === "ok" && (
        <p className="mt-1 text-[10.5px] text-emerald-700 font-semibold flex items-center gap-1" data-testid="auth-partner-ok">
          <Check className="w-2.5 h-2.5" /> {status.message}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// SetPinStep — used by SignupFlow after OTP verify, and by PinMigrationGate
// for legacy accounts on their first login post-Feb 2026.
// =============================================================================
export function SetPinStep({ session, onDone }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!/^\d{4}$/.test(pin)) { setErr("PIN must be exactly 4 digits"); return; }
    if (pin !== confirmPin) { setErr("PINs don't match"); return; }
    // Simple guard against trivially weak PINs (all-same digit + straight
    // sequences). Not user-configurable — we just show a warning and let
    // them proceed, since 4-digit-strength is a shallow speed-bump anyway.
    if (/^(\d)\1{3}$/.test(pin)) {
      setErr("Pick a less-obvious PIN (not 1111 / 2222 …)");
      return;
    }
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API}/auth/pin/set`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ pin }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setErr(data.detail || "Could not set PIN");
        return;
      }
      onDone?.();
    } catch (e2) {
      setErr(e2?.message || "Could not set PIN");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="auth-set-pin-step">
      <div className="rounded-lg border border-violet-300 bg-violet-50 p-2.5 text-[11.5px] text-violet-900 flex items-start gap-2">
        <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold">Almost done. Pick a 4-digit PIN.</p>
          <p className="mt-0.5">You&apos;ll use this to sign in from now on — no more OTPs.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-[10.5px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">New PIN</p>
          <Input
            type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
            placeholder="••••" value={pin}
            onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
            autoComplete="new-password"
            className="h-11 text-center text-lg font-mono tracking-[0.5em]"
            data-testid="auth-set-pin-input" autoFocus
          />
        </div>
        <div>
          <p className="text-[10.5px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">Confirm PIN</p>
          <Input
            type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
            placeholder="••••" value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
            autoComplete="new-password"
            className="h-11 text-center text-lg font-mono tracking-[0.5em]"
            data-testid="auth-set-pin-confirm-input"
          />
        </div>
      </div>
      {err && <p className="text-[11px] text-rose-700" data-testid="auth-set-pin-error">{err}</p>}
      <Button
        type="submit"
        className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
        disabled={busy || pin.length !== 4 || confirmPin.length !== 4}
        data-testid="auth-set-pin-submit"
      >
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {busy ? "Saving PIN…" : "Set PIN & continue"}
      </Button>
    </form>
  );
}

// Ambient trust footer (kept exported for possible reuse; unused inside sleek modal)
export function TrustFooter() {
  return (
    <div className="hidden sm:flex items-center gap-2 text-[11px] text-slate-500 pt-3 border-t border-slate-100">
      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
      Sign in fast — 4-digit PIN or Google.
    </div>
  );
}
